<template>
  <div
    :ref="'konva_popover'"
    :style="{
      top: adjustedPosition.y + 'px',
      left: adjustedPosition.x + 'px',
      borderRadius: '3px',
      boxShadow: '0 0 5px grey',
      backgroundColor: '#fff',
      border: '2px solid #5577bb',
      width: popoverWidth + 'px',
      maxWidth: 'calc(100vw - 20px)',
      zIndex: 99999,
      position: 'fixed'
    }"

    class="rounded overflow-hidden shadow-lg"
  >
    <!-- Cabecera siempre visible -->
    <div class="font-semibold px-3 py-2 mb-0 uppercase flex items-center justify-between text-white" style="background-color: #5577bb;">
      <span>{{ $t(type) }} : {{ displayName }}</span>
      <button
        @click="handleClose"
        class="ml-2 text-white hover:text-gray-200 focus:outline-none transition-colors"
        type="button"
        :aria-label="$t('close')"
      >
        <b-icon icon="times" pack="fas" size="is-small"></b-icon>
      </button>
    </div>

    <!-- Loader dentro del contenido -->
    <div v-if="isLoading" class="flex items-center justify-center px-4 py-8">
      <b-icon icon="spinner" pack="fas" size="is-medium" class="fa-spin mr-2"></b-icon>
      <span class="text-sm text-gray-600">{{ $t('loading_information') }}</span>
    </div>

    <!-- Contenido cuando no está cargando -->
    <template v-else>
      <!-- Estado/Incidencia Actual -->
      <!-- Tabs para Histórico -->
      <div class="card-body px-0 py-0" style="width: 100%;">
        <b-tabs type="is-boxed" size="is-small" v-model="activeTab" style="width: 100%;">
          <!-- Tab Progreso -->
          <b-tab-item>
            <template slot="header">
              <b-icon icon="history" pack="fas" size="is-small"></b-icon>
              <span class="ml-1">{{ $t('progress') }}</span>
            </template>
            <div class="card-body px-2 py-1 border-b border-solid border-slate-100">
              <div class="flex flex-row mb-2-x">
                <div class="flex-1">
                  <p class="text-sm flex items-center">
                    <span
                      class="inline-flex items-center justify-center w-3 h-3 rounded-full border-2 mr-2"
                      :style="{
                        backgroundColor: _getCurrentStatusColor(LayerStatusType.PROGRESS),
                        borderColor: _getCurrentStatusColor(LayerStatusType.PROGRESS),
                        boxShadow: '0 0 0 1px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.1)'
                      }"
                    >
                    </span>
                    <strong>{{ $t('current_status') }}</strong>
                    <span class="ml-1">{{ _getCurrentStatusName(LayerStatusType.PROGRESS) }}</span>
                  </p>
                </div>
              </div>
            </div>
            <div class="px-2 py-2" style="max-height: 200px; overflow-y: auto; width: 100%;">
              <div v-if="history.progress.length === 0" class="text-sm text-gray-500 text-center py-2">
                {{ $t('no_progress_history') }}
              </div>
              <div v-else>
                <div
                  v-for="(item, index) in history.progress"
                  :key="'progress-' + index"
                  class="flex items-start mb-2 pb-2 border-b border-gray-100 last:border-0"
                  style="width: 100%;"
                >
                  <div class="flex-shrink-0 mr-2 flex items-center justify-center">
                    <div
                      class="w-3 h-3 rounded-full border-2 flex items-center justify-center"
                      :style="{
                        backgroundColor: item.state_color || '#666',
                        borderColor: item.state_color || '#666',
                        boxShadow: '0 0 0 1px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.1)'
                      }"
                    >
                    </div>
                  </div>
                  <div class="flex-1 text-xs" style="min-width: 0; word-wrap: break-word;">
                    <div class="font-semibold">{{ item.state_name }}</div>
                    <div class="text-gray-500">{{ item.created_at }}</div>
                    <div class="flex items-center mt-1 text-gray-400 text-xs">
                      <div v-if="item.user && item.user.avatar" class="flex-shrink-0 mr-1.5">
                        <img
                          :src="item.user.avatar"
                          :alt="item.user.fullname || $t('user')"
                          class="w-5 h-5 rounded-full border border-gray-300 object-cover"
                        />
                      </div>
                      <div class="flex-1" style="min-width: 0;">
                        <span>{{ item.user?.fullname || $t('user') }}</span>
                        <span v-if="item.user?.area" class="text-gray-500"> ({{ item.user.area }})</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </b-tab-item>

          <!-- Tab Incidencias -->
          <b-tab-item>
            <template slot="header">
              <b-icon icon="exclamation-triangle" pack="fas" size="is-small"></b-icon>
              <span class="ml-1">{{ $t('issue') }}</span>
            </template>
            <div v-if="dataTooltip.attrs && dataTooltip.attrs.incident && currentStatus && currentStatus.incident"
              class="card-body px-2 py-1 border-b border-solid border-slate-100">
              <div class="flex flex-row">
                <div class="flex-1">
                  <p class="text-sm flex items-center">
                    <span
                      class="inline-flex items-center justify-center w-3 h-3 rounded-full border-2 mr-2"
                      :style="{
                        backgroundColor: _getCurrentStatusColor(LayerStatusType.INCIDENT),
                        borderColor: _getCurrentStatusColor(LayerStatusType.INCIDENT),
                        boxShadow: '0 0 0 1px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.1)'
                      }"
                    >
                    </span>
                    <strong>{{ $t('current_issue') }}</strong>
                    <span class="ml-1">{{ _getCurrentStatusName(LayerStatusType.INCIDENT) }}</span>
                  </p>
                </div>
              </div>
            </div>
            <div class="px-2 py-2" style="max-height: 200px; overflow-y: auto; width: 100%;">
              <div v-if="history.incidents.length === 0" class="text-sm text-gray-500 text-center py-2">
                {{ $t('no_incident_history') }}
              </div>
              <div v-else>
                <div
                  v-for="(item, index) in history.incidents"
                  :key="'incident-' + index"
                  class="flex items-start mb-2 pb-2 border-b border-gray-100 last:border-0"
                  style="width: 100%;"
                >
                  <div class="flex-shrink-0 mr-2 flex items-center justify-center">
                    <div
                      class="w-3 h-3 rounded-full border-2 flex items-center justify-center"
                      :style="{
                        backgroundColor: item.state_color || '#dc2626',
                        borderColor: item.state_color || '#dc2626',
                        boxShadow: '0 0 0 1px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.1)'
                      }"
                    >
                    </div>
                  </div>
                  <div class="flex-1 text-xs" style="min-width: 0; word-wrap: break-word;">
                    <div class="font-semibold">{{ item.state_name }}</div>
                    <div class="text-gray-500">{{ item.created_at }}</div>
                    <div class="flex items-center mt-1 text-gray-400 text-xs">
                      <div v-if="item.user && item.user.avatar" class="flex-shrink-0 mr-1.5">
                        <img
                          :src="item.user.avatar"
                          :alt="item.user.fullname || $t('user')"
                          class="w-5 h-5 rounded-full border border-gray-300 object-cover"
                        />
                      </div>
                      <div class="flex-1" style="min-width: 0;">
                        <span>{{ item.user?.fullname || $t('user') }}</span>
                        <span v-if="item.user?.area" class="text-gray-500"> ({{ item.user.area }})</span>
                      </div>
                    </div>
                    <div v-if="item.description" class="text-gray-600 mt-1 italic" style="word-wrap: break-word;">
                      {{ item.description ?? '' }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </b-tab-item>

          <!-- Tab Certificación (solo si el elemento ya está certificado — dato real de Mongo) -->
          <b-tab-item v-if="certification">
            <template #header>
              <b-icon icon="certificate" pack="fas" size="is-small"></b-icon>
              <span class="ml-1">{{ $t('certification') }}</span>
            </template>

            <div class="px-3 py-3">
              <div class="cert-certified-badge">
                <b-icon icon="check-circle" pack="fas" size="is-small"></b-icon>
                <span>{{ $t('already_certified') }}</span>
              </div>

              <ul class="cert-info-list">
                <li v-if="certification.certification_number" class="cert-info-row">
                  <span class="cert-info-label">{{ $t('certification_number') }}</span>
                  <span class="cert-info-value">{{ certSequence(certification.certification_number) }}</span>
                </li>
                <li class="cert-info-row">
                  <span class="cert-info-label">{{ $t('work_order') }}</span>
                  <span class="cert-info-value">{{ certification.order_code || '-' }}</span>
                </li>
                <li class="cert-info-row">
                  <span class="cert-info-label">{{ $t('date') }}</span>
                  <span class="cert-info-value">{{ certification.date || '-' }}</span>
                </li>
                <li class="cert-info-row">
                  <span class="cert-info-label">{{ $t('contractor') }}</span>
                  <span class="cert-info-value">{{ certification.contractor || '-' }}</span>
                </li>
              </ul>

              <a
                v-if="certification.validation_flow_id"
                :href="certificationFlowUrl(certification.validation_flow_id)"
                target="_blank"
                rel="noopener"
                class="cert-flow-link"
              >
                <b-icon icon="external-link-alt" pack="fas" size="is-small"></b-icon>
                <span>{{ $t('view_certification_flow') }}</span>
              </a>
            </div>
          </b-tab-item>

          <!-- Tab Atributos extra -->
          <b-tab-item :disabled="extraAttributesList.length === 0">
            <template #header>
              <b-icon icon="list-ul" pack="fas" size="is-small"></b-icon>
              <span class="ml-1">{{ $t('attributes') }}</span>
            </template>

            <div v-if="extraAttributesList.length === 0" class="text-sm text-gray-500 text-center py-3">
              {{ $t('no_extra_attributes') }}
            </div>

            <ul v-else class="extra-attrs-list">
              <li
                v-for="item in extraAttributesList"
                :key="item.key"
                class="extra-attrs-row"
              >
                <span class="extra-attrs-label">{{ item.label }}</span>
                <span class="extra-attrs-value">{{ item.value }}</span>
              </li>
            </ul>
          </b-tab-item>
        </b-tabs>
      </div>
    </template>

<!--
    <div class="card-body px-3 py-2">
      <label for="weight" class="label">
        {{ $t('status') }}
      </label>
      <b-select v-model="selectedStatus"
        placeholder="Elija estado"
        icon="traffic-light"
        icon-pack="fas"
        expanded
        @input="handleChangeStatus">
        <option :value="null" key="status_null">Sin estado</option>
        <option v-for="option in statuses"
          :value="option"
          :key="'status_' + option.id">
          {{ option.name }}
        </option>
      </b-select>
    </div> -->
  </div>
</template>

<script>
import axios from 'axios';

/** Events: all DxfViewer supported events (see DxfViewer.Subscribe()), prefixed with "dxf-". */
import { LayerStatusType } from '../viewer-pixi/constants/LayerStatusType';
import { IMPORT_EXTRA_ATTRS_COLUMNS } from './layers/specs/importSpec';

export default {
    name: "KonvaViewerPopover",

    props: {
        position: { type: Object, required: true },
        dataTooltip: { type: Object, default() { return { } }},
        statuses: { type: Array, default() { return [] } },
    },

    data() {
        return {
          selectedStatus: null,
          isLoading: true,
          activeTab: 0,
          // Datos ficticios para el histórico de progreso
          history: {
            progress: [],
            incidents: [],
          },
          // Exponemos las constantes de tipo de estado de la capa
          LayerStatusType,
          currentStatus: {
            progress: null,
            incident: null,
          },
          extraAttributes: null,   // null = no cargado todavía, {} = cargado vacío, {...} = con datos
          componentMetadata: null,   // { name, code } de Mongo, o null si no se cargó
          // Certificación real del elemento (resuelta en backend desde certifications[] +
          // CertificationValidationFlow). null = el elemento no está certificado → pestaña oculta.
          certification: null,
          // Dimensiones del popover para cálculo de posición
          popoverWidth: 380,
          popoverHeight: 250, // altura inicial estimada, se actualiza dinámicamente
          windowWidth: window.innerWidth,
          windowHeight: window.innerHeight,
        }
    },

    computed: {
      type: function() {
        return this.dataTooltip.attrs ? this.dataTooltip.attrs.type : 'structure'
      },

      /**
       * Calcula la posición ajustada del popover para mantenerlo dentro del viewport
       */
      adjustedPosition() {
        // Si la posición es muy negativa, es intencional para ocultar el popover
        if (this.position.x < -1000) {
          return { x: this.position.x, y: this.position.y };
        }

        const margin = 10; // margen mínimo del borde de la ventana

        let x = this.position.x + 2;
        let y = this.position.y + 2;

        // Ajuste horizontal: si se sale por la derecha, mostrar hacia la izquierda
        if (x + this.popoverWidth > this.windowWidth - margin) {
          x = this.position.x - this.popoverWidth - 2;
        }

        // Si aún se sale por la izquierda, forzar al margen izquierdo
        if (x < margin) {
          x = margin;
        }

        // Ajuste vertical: si se sale por abajo, mostrar hacia arriba
        if (y + this.popoverHeight > this.windowHeight - margin) {
          y = this.position.y - this.popoverHeight - 2;
        }

        // Si aún se sale por arriba, forzar al margen superior
        if (y < margin) {
          y = margin;
        }

        return { x, y };
      },

      extraAttributesList() {
        if (!this.extraAttributes) return [];

        // Filtramos las metadata de auditoría y los nulls/vacíos
        const omit = new Set(['updated_at', 'updated_by']);

        return IMPORT_EXTRA_ATTRS_COLUMNS
          .filter(col => col.key !== 'id' && !omit.has(col.key))
          .map(col => ({
            key:   col.key,
            label: col.label,
            value: this.extraAttributes[col.key] ?? null,
          }))
          .filter(item => item.value !== null && item.value !== '');
      },

      /**
       * Prioridad para mostrar en el header:
       *   1. componentMetadata.code  (CT01.T001.E1 — más contextual)
       *   2. componentMetadata.name  (E1 — más corto)
       *   3. dataTooltip.attrs.name  (E.P.8 — fallback generado en canvas)
       *
       * Se considera "no presente" tanto null como string vacío. Cualquier otro
       * valor (incluido "0", aunque improbable) cuenta como válido.
       */
      displayName() {
        const code = this.componentMetadata?.code;
        if (code !== null && code !== undefined && code !== '') {
          return code;
        }

        const name = this.componentMetadata?.name;
        if (name !== null && name !== undefined && name !== '') {
          return name;
        }

        return this.dataTooltip?.attrs?.name ?? '';
      },
    },

    watch: {
      dataTooltip(nuevoValor, valorAnterior) {
        this.selectedStatus = nuevoValor.status == null ? null : nuevoValor.status
        // Cargar información cuando cambia el tooltip
        if (nuevoValor.attrs && nuevoValor.attrs.id) {
          this.fetchComponentData(nuevoValor.attrs.id)
        }
      }
    },

    created() {

    },

    updated() {
      // Actualizar la altura real del popover después de cada render
      this.updatePopoverHeight();
    },

    mounted() {
      // Agregar listener para cerrar al hacer clic fuera del popover
      // Usar setTimeout para evitar que el clic que abre el popover también lo cierre
      setTimeout(() => {
        document.addEventListener("click", this.onClickOutside);
      }, 100);

      // Listener para actualizar dimensiones de ventana al resize
      window.addEventListener("resize", this.onWindowResize);
    },

    methods: {
      /**
       * Obtiene la información del componente desde el endpoint
       * Por ahora solo simula la carga, la lógica del endpoint se implementará después
       */
      async fetchComponentData(componentId) {
        this.isLoading = true

        this.history.progress = []
        this.history.incidents = []
        this.extraAttributes   = null;
        this.componentMetadata = null;
        this.certification     = null;

        try {
          const params = {
            work_lyr: this.dataTooltip.attrs.type,
          }
          const { data } = await axios.get(`/api/v2/pictogram/${this.dataTooltip.attrs.pictogramId}/component/${componentId}`, { params: params })
          if (data && data.success) {
            this.history = data.component.history || { progress: [], incidents: [] }
            this.currentStatus = data.component.current_status || { progress: null, incident: null };
            this.extraAttributes = data.component.extra_attributes || null;
            this.componentMetadata = data.component.metadata         || null;
            this.certification     = data.component.certification    || null;
          }
        } catch (error) {
          console.error('Error al cargar información del componente:', error)
          // En caso de error, mantener los datos ficticios o mostrar mensaje
          this.$toast?.open({
            message: this.$t('error_loading_component_info'),
            type: 'is-danger',
            position: 'is-top-right'
          })
        } finally {
          this.isLoading = false
          // Medir altura después de que el contenido se renderice
          this.$nextTick(() => {
            this.updatePopoverHeight();
          });
        }
      },

      /**
       * CERTIFICACIÓN Nº: extrae el número de secuencia final del código de certificación
       * (último segmento tras '-') y lo devuelve sin ceros a la izquierda. El resto es el prefijo
       * (nº de pedido). Ej.: 'PV01-MM-B01-HIN-26-0012-CHECK-0001' -> '1'. Misma lógica que el PDF.
       */
      certSequence(certification) {
        if (!certification) return ''
        const parts = String(certification).split('-')
        const last = parts[parts.length - 1]
        return /^\d+$/.test(last) ? String(parseInt(last, 10)) : String(certification)
      },

      /**
       * URL del flujo de certificación (ruta 'validation.home' → /validation/:validation_id).
       * Se usa en un <a target="_blank"> para abrirlo en otra pestaña sin sacar al usuario del visor.
       */
      certificationFlowUrl(flowId) {
        if (!flowId) return '#'
        try {
          return this.$router.resolve({ name: 'validation.home', params: { validation_id: flowId } }).href
        } catch (e) {
          return `/validation/${flowId}`
        }
      },

      handleClose() {
        this.$emit('epc-close');
      },

      /**
       * Actualiza las dimensiones de la ventana cuando cambia el tamaño
       */
      onWindowResize() {
        this.windowWidth = window.innerWidth;
        this.windowHeight = window.innerHeight;
      },

      /**
       * Mide y actualiza la altura real del popover
       */
      updatePopoverHeight() {
        const popoverElement = this.$refs.konva_popover;
        if (popoverElement && popoverElement.offsetHeight > 0) {
          this.popoverHeight = popoverElement.offsetHeight;
        }
      },

      onClickOutside(e) {
        const popoverElement = this.$refs.konva_popover;
        if (popoverElement && !this.isClickedInside(e, popoverElement)) {
          this.$emit('epc-close');
        }
      },

      isClickedInside(e, element) {
        if (!element) return false;
        let node = e.target;
        while (node) {
          if (node === element) {
            return true;
          }
          node = node.parentNode;
        }
        return false;
      },

      handleChangeStatus(value) {

        this.dataTooltip.fill( value?.color ?? null )
        // let statusOld = JSON.parse(JSON.stringify(this.dataTooltip.status)) //clo

        this.dataTooltip.status = value

        // colocar aqui se se quiere emitir alguna llamada a la api

        this.$emit('epc-update-list', this.dataTooltip)
        this.$emit('epc-close')
      },

      _getCurrentStatusName(typeStatus) {
        const defaultName =  typeStatus === 'progress' ? this.$t('no_status_2') : this.$t('without_issue');
        if (this.currentStatus[typeStatus]) {
          return this.currentStatus[typeStatus].state_name ?? deafultName;
        }

        return defaultName;
        // return this.statuses.find( (item) => { return item.id === status } )?.name ?? 'Sin estado';
      },

      _getCurrentStatusColor(typeStatus) {

        if (this.currentStatus[typeStatus]) {
          return this.currentStatus[typeStatus].state_color ?? '#666';
        }

        return '#666';
        // return this.statuses.find( (item) => { return item.id === status } )?.color ?? ''
      },

      _getIncidentName(incident) {
        return this.incidents.find( (item) => { return item.id === incident } )?.name ?? ''
      },

      _getIncidentColor(incident) {
        return this.incidents.find( (item) => { return item.id === incident } )?.color ?? ''
      },
    },

    beforeDestroy() {
      // Remover listeners al destruir el componente
      document.removeEventListener("click", this.onClickOutside);
      window.removeEventListener("resize", this.onWindowResize);
    },
}
</script>

<style scoped>

.extra-attrs-list {
    list-style: none;
    margin: 0;
    padding: 0;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    overflow: hidden;
  }

  .extra-attrs-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    border-bottom: 1px solid #f3f4f6;
    font-size: 12px;
  }

  .extra-attrs-row:last-child {
    border-bottom: none;
  }

  .extra-attrs-row:nth-child(odd) {
    background-color: #f9fafb;
  }

  .extra-attrs-label {
    color: #6b7280;
    font-weight: 500;
    flex-shrink: 0;
    margin-right: 12px;
  }

  .extra-attrs-value {
    color: #111827;
    font-weight: 600;
    text-align: right;
    word-break: break-word;
  }

  /* Badge "ya certificado": verde legible (pill con fondo), en vez del verde sutil anterior */
  .cert-certified-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 12px;
    padding: 4px 12px;
    border-radius: 999px;
    background: #dcfce7;
    color: #15803d;
    border: 1px solid #86efac;
    font-weight: 700;
    font-size: 12px;
  }

  /* Link al flujo de certificación (abre en otra pestaña) */
  .cert-flow-link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-top: 12px;
    padding: 6px 12px;
    border-radius: 6px;
    background: #eff6ff;
    color: #1d4ed8;
    border: 1px solid #bfdbfe;
    font-weight: 600;
    font-size: 12px;
    text-decoration: none;
    transition: background-color 150ms ease, border-color 150ms ease;
  }
  .cert-flow-link:hover {
    background: #dbeafe;
    border-color: #93c5fd;
  }

  .cert-info-list {
    list-style: none;
    margin: 0;
    padding: 0;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    overflow: hidden;
  }

  .cert-info-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    border-bottom: 1px solid #f3f4f6;
    font-size: 12px;
  }

  .cert-info-row:last-child {
    border-bottom: none;
  }

  .cert-info-row:nth-child(odd) {
    background-color: #f9fafb;
  }

  .cert-info-label {
    color: #6b7280;
    font-weight: 500;
    flex-shrink: 0;
    margin-right: 12px;
  }

  .cert-info-value {
    color: #111827;
    font-weight: 600;
    text-align: right;
    word-break: break-word;
  }

</style>
